import React from 'react';
import groundImg from './cricket-ground.png';

const groundStyle = {
  wrapper: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px 0',
  },
  divisionName: {
    color: '#090C82',
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '4px',
  },
  gameTitle: {
    color: '#090C82',
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '4px',
  },
  gameDescription: {
    fontSize: '0.95rem',
    color: '#333',
    marginBottom: '24px',
  },
  groundContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto 30px',
  },
  groundImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  playerName: {
    position: 'absolute',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    textAlign: 'center',
    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
    whiteSpace: 'nowrap',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(0,0,0,0.45)',
    padding: '3px 8px',
    borderRadius: '3px',
  },
  sectionLine: {
    fontSize: '0.9rem',
    marginBottom: '6px',
    color: '#333',
  },
  sectionLabel: {
    fontWeight: 700,
  },
  sectionNames: {
    color: '#090C82',
  },
};

/*
 * Cricket fielding positions mapped to percentage coordinates on the circular ground.
 * Order matches CRICKET_POSITIONS in fieldGrid.js:
 *   Row 1: Wicketkeeper, Bowler
 *   Row 2: Slip, Gully, Point
 *   Row 3: Cover, Mid Off, Mid On
 *   Row 4: Mid Wicket, Square Leg, Fine Leg
 */
const POSITION_COORDS = [
  // Row 1: Wicketkeeper (behind stumps bottom), Bowler (top of pitch)
  { top: '58%', left: '50%' },
  { top: '38%', left: '50%' },
  // Row 2: Slip (behind wicket, off side), Gully (wider off side), Point (square off side)
  { top: '62%', left: '58%' },
  { top: '55%', left: '68%' },
  { top: '48%', left: '78%' },
  // Row 3: Cover (off side front), Mid Off (straight off side), Mid On (straight leg side)
  { top: '35%', left: '72%' },
  { top: '22%', left: '58%' },
  { top: '22%', left: '42%' },
  // Row 4: Mid Wicket (leg side front), Square Leg (square leg side), Fine Leg (behind, leg side)
  { top: '35%', left: '28%' },
  { top: '48%', left: '22%' },
  { top: '68%', left: '30%' },
];

function formatPlayerLabel(player) {
  if (!player || !player.name) return '';
  let label = player.name;
  if (player.number) label += ' #' + player.number;
  if (player.isCaptain) label += ' (c)';
  return label;
}

function formatListSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => formatPlayerLabel(p))
    .join(',  ');
}

function formatOutSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => {
      let label = p.name;
      if (p.number) label += ' #' + p.number;
      if (p.reason) label += ' (' + p.reason + ')';
      if (p.isCaptain) label += ' (c)';
      return label;
    })
    .join(',  ');
}

export function CricketGround({ divisionName, gameTitle, gameDescription, selectionData }) {
  const field = selectionData.field || [];
  const interchange = selectionData.interchange || [];
  const playerIn = selectionData.in || [];
  const playerOut = selectionData.out || [];

  return (
    <div style={groundStyle.wrapper}>
      {divisionName && (
        <div style={groundStyle.divisionName}>{divisionName} Team Selections</div>
      )}
      {gameTitle && <div style={groundStyle.gameTitle}>{gameTitle}</div>}
      {gameDescription && <div style={groundStyle.gameDescription}>{gameDescription}</div>}

      <div style={groundStyle.groundContainer}>
        <img src={groundImg} alt="Cricket ground" style={groundStyle.groundImage} />
        {field.map((player, idx) => {
          const label = formatPlayerLabel(player);
          if (!label || idx >= POSITION_COORDS.length) return null;
          const coords = POSITION_COORDS[idx];
          return (
            <div
              key={idx}
              style={{ ...groundStyle.playerName, top: coords.top, left: coords.left }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {interchange.length > 0 && (
        <div style={groundStyle.sectionLine}>
          <span style={groundStyle.sectionLabel}>12th Man - </span>
          <span style={groundStyle.sectionNames}>{formatListSection(interchange)}</span>
        </div>
      )}
      {playerIn.length > 0 && (
        <div style={groundStyle.sectionLine}>
          <span style={groundStyle.sectionLabel}>In - </span>
          <span style={groundStyle.sectionNames}>{formatListSection(playerIn)}</span>
        </div>
      )}
      {playerOut.length > 0 && (
        <div style={groundStyle.sectionLine}>
          <span style={groundStyle.sectionLabel}>Out - </span>
          <span style={groundStyle.sectionNames}>{formatOutSection(playerOut)}</span>
        </div>
      )}
    </div>
  );
}
